import React, { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { ColumnDef, PaginationState, SortingState } from '@tanstack/react-table';
import { Plus, Search, Pencil, Eye, Trash2 } from 'lucide-react';
import { DataTable } from '@/components/ui/data-table';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ActiveStatusBadge } from '@/components/ui/status-badge';
import { Rating } from '@/components/ui/rating';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useVendors, useDeleteVendor, type VendorListFilters } from '../services/vendorService';
import type { VendorListItem } from '../types';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Format currency value in USD
 */
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Vendor List Page Component
 *
 * Displays a searchable, sortable, and filterable data table of all vendors with:
 * - Server-side pagination
 * - Real-time search with debouncing
 * - Active status filtering
 * - Criticality rating display
 * - Row actions (view, edit, delete)
 * - Navigation to detail and form pages
 */
const VendorList: React.FC = () => {
  const navigate = useNavigate();
  const { canManageVendors } = useAuth();
  const deleteVendor = useDeleteVendor();

  // State for filters and pagination
  const [filters, setFilters] = useState<VendorListFilters>({
    search: '',
    isActive: undefined,
    pageNumber: 1,
    pageSize: 25,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  // Debounced search term
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination and sorting state for DataTable
  const pagination: PaginationState = {
    pageIndex: (filters.pageNumber || 1) - 1,
    pageSize: filters.pageSize || 25,
  };

  const sorting: SortingState = filters.sortBy
    ? [{ id: filters.sortBy, desc: filters.sortOrder === 'desc' }]
    : [];

  // Fetch vendors with current filters
  const { data, isLoading, error, isError } = useVendors(filters);

  /**
   * Handle search input change with debouncing
   */
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);

    // Debounce search to avoid excessive API calls
    const timeoutId = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        search: value,
        pageNumber: 1,
      }));
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  /**
   * Handle active filter toggle
   */
  const handleActiveFilterChange = (checked: boolean) => {
    setFilters((prev) => ({
      ...prev,
      isActive: checked ? true : undefined,
      pageNumber: 1,
    }));
  };

  /**
   * Handle pagination change
   */
  const handlePaginationChange = (newPagination: PaginationState) => {
    setFilters((prev) => ({
      ...prev,
      pageNumber: newPagination.pageIndex + 1,
      pageSize: newPagination.pageSize,
    }));
  };

  /**
   * Handle sorting change
   */
  const handleSortingChange = (newSorting: SortingState) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: newSorting[0]?.id || 'name',
      sortOrder: newSorting[0]?.desc ? 'desc' : 'asc',
    }));
  };

  /**
   * Handle row click navigation to detail page
   */
  const handleRowClick = (row: VendorListItem) => {
    navigate({ to: `/vendors/${row.id}` });
  };

  /**
   * Handle delete vendor
   */
  const handleDelete = async (id: string, name: string, event: React.MouseEvent) => {
    event.stopPropagation();

    if (window.confirm(`Are you sure you want to delete vendor "${name}"?`)) {
      try {
        await deleteVendor.mutateAsync(id);
      } catch (error) {
        console.error('Failed to delete vendor:', error);
        alert('Failed to delete vendor. Please try again.');
      }
    }
  };

  /**
   * DataTable column definitions
   */
  const columns: ColumnDef<VendorListItem>[] = [
    {
      accessorKey: 'name',
      header: 'Vendor Name',
    },
    {
      accessorKey: 'primaryContactName',
      header: 'Primary Contact',
    },
    {
      accessorKey: 'primaryContactEmail',
      header: 'Email',
    },
    {
      accessorKey: 'activeContracts',
      header: 'Active Contracts',
      cell: ({ row }) => (
        <span className="text-center block">{row.original.activeContracts}</span>
      ),
    },
    {
      accessorKey: 'totalContractValue',
      header: 'Total Value',
      cell: ({ row }) => formatCurrency(row.original.totalContractValue),
    },
    {
      accessorKey: 'criticalityRating',
      header: 'Criticality',
      cell: ({ row }) => <Rating value={row.original.criticalityRating} size="sm" />,
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="View vendor"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate({ to: `/vendors/${row.original.id}` });
                }}
              >
                <Eye className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>View Details</TooltipContent>
          </Tooltip>
          {canManageVendors && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Edit vendor"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate({ to: `/vendors/${row.original.id}/edit` });
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Delete vendor"
                    className="text-destructive hover:text-destructive"
                    onClick={(e) => handleDelete(row.original.id, row.original.name, e)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete</TooltipContent>
              </Tooltip>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      {/* Page Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-semibold text-grey-800">Vendors</h1>
        {canManageVendors && (
          <Button size="lg" onClick={() => navigate({ to: '/vendors/new' })}>
            <Plus className="h-4 w-4" />
            New Vendor
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3 items-center">
          <div className="relative flex-1 min-w-[300px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
            <Input
              placeholder="Search vendors..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={filters.isActive === true}
              onCheckedChange={handleActiveFilterChange}
              id="active-filter"
            />
            <Label htmlFor="active-filter">Active Only</Label>
          </div>
        </div>
      </Card>

      {/* Error Display */}
      {isError && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            Failed to load vendors: {error?.message || 'Unknown error'}
          </AlertDescription>
        </Alert>
      )}

      {/* Data Table */}
      <Card>
        <DataTable
          columns={columns}
          data={data?.items || []}
          rowCount={data?.totalCount || 0}
          isLoading={isLoading}
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          onRowClick={handleRowClick}
          emptyMessage="No vendors found"
        />
      </Card>
    </div>
  );
};

export default VendorList;
